import { Checkbox } from "@fluentui/react";
import React from "react";
import { filesCreate, filesUpdate } from "../../../api/api";
import { useTools } from "../../../state/api/files/useTools";
import { useProducts } from "../../../state/api/products/useProducts";
import { useSession } from "../../../state/api/session/useSession";
import { Doc } from "../../../types/db/collections";
import { File, FILE_TYPE, FILE_TYPE_LABELS, GetFileResponse } from "../../../types/db/schema/files/file.schema";
import Button, { ButtonBase } from "../buttons/Button";
import Submit from "../buttons/Submit";
import FieldWithLabel from "../formik/FieldWithLabel";
import { Dialog, DialogBody, DialogFooter, ModalForm } from "../modal/ModalForm";
import FileSelect from "./FileSelect";

interface Props extends ButtonBase {
  file?: Partial<Doc<GetFileResponse>> & { product: string }
}

export default function FileDialog(props: Props) {

  const { session } = useSession();

  if (!session?.isAdmin) {
    return null;
  }

  return (
    <Dialog
      trigger={(
        <Button
          variant={props.file?._id ? "ghost" : undefined}
          size={props.file?._id ? "small" : "regular"}
          text={props.file?._id ? "Edit" : "Add File"}
          icon={props.file?._id ? "pen" : "file-earmark-plus"}
          {...props}
        />
      )}
      dialog={<FileDialogInner {...props} />}
    />
  )
}

function FileDialogInner(props: Props) {

  const [filename, setFilename] = React.useState(props.file?.filename || "");

  const {
    file
  } = props;

  const { mutate: reloadTools } = useTools({ enabled: false });
  const { mutate: reloadProducts } = useProducts({ enabled: false });

  return (
    <ModalForm
      title={file?._id ? "Edit File" : "Add File"}
      description={file?._id ? `Edit file '${file.name}'` : "Add a file to this product"}
      initialValues={{
        content: "",
        name: "",
        filename: "",
        type: FILE_TYPE.MODEL,
        isActive: true,
        showIsNew: true,
        version: "",
        description: "",
        ...file
      } as File}
      onSubmit={async (values, callApi) => {
        const res = await callApi(
          file?._id
            ? filesUpdate(values as Doc<File>)
            : filesCreate(values)
        )

        if (!res?.success) {
          return false;
        }

        await reloadProducts();
        await reloadTools();

        return true;
      }}
    >
      {
        ({ values, setFieldValue }) => (
          <>
            <DialogBody>
              <FileSelect
                accept=".xls*"
                onChange={(f, content) => {
                  setFieldValue("content", content);
                  setFieldValue("filename", f.name);
                  setFilename(f.name);
                }}
                selectedFileName={filename}
              />
              <div className="d-flex flex-row gap-2">
                <FieldWithLabel name="name" label="Display Name" className="w-100" />
                <FieldWithLabel name="version" label="Version" className="w-100" />
              </div>
              <FieldWithLabel name="filename" label="Filename" placeholder="File is downloaded as..." />
              <div className="d-flex flex-row gap-2">
                <FieldWithLabel name="description" className="w-100" label="Description" />
                <FieldWithLabel type="number" className="w-50" name="order" label="List Position" />
              </div>
              <div className="d-flex flex-column gap-1">
                <label>Type</label>
                <div className="d-flex flex-row gap-2">
                  {Object.values(FILE_TYPE).map(t => (
                    <Button
                      key={t}
                      icon={values.type === t ? "check" : "tag"}
                      color={values.type === t ? undefined : "var(--background)"}
                      text={FILE_TYPE_LABELS[t]}
                      onClick={() => setFieldValue("type", t)}
                    />
                  ))}
                </div>
              </div>
              <div className="d-flex flex-row">
                <div className="d-flex flex-column w-100 gap-1">
                  <label>Status</label>
                  <Checkbox
                    styles={{ checkbox: { height: "16px", width: "16px" } }}
                    onRenderLabel={() => <strong className="ms-1" style={{ fontSize: "11.5px", color: "var(--gray-900)" }}>Visible to users</strong>}
                    checked={values.isActive}
                    onChange={(_, v) => setFieldValue("isActive", v)}
                  />
                </div>
                <div className="d-flex flex-column w-100 gap-1">
                  <label>Recent Update</label>
                  <Checkbox
                    styles={{ checkbox: { height: "16px", width: "16px" } }}
                    onRenderLabel={() => <strong className="ms-1" style={{ fontSize: "11.5px", color: "var(--gray-900)" }}>Show NEW Badge</strong>}
                    checked={values.showIsNew}
                    onChange={(_, v) => setFieldValue("showIsNew", v)}
                  />
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Submit />
            </DialogFooter>
          </>
        )
      }
    </ModalForm>
  )
}